import { useMutation, useQuery, useQueryClient } from "react-query";
import { HttpBaseReq } from "../../../core/HttpRequest";
import {
  deleteUser,
  getUserRoles,
  getUserDetail,
  getUsers,
  patchUser,
  postAddUser,
} from "./service";
import { useToast } from "../../../context/toast/ToastContext";
import { IHttpPatchUser, IHttpPostUser } from "../type/user.http";
import { AxiosError } from "axios";
import { useErrorHandler } from "../../../hooks/useErrorHandler";
import { useNavigate } from "react-router";
import { TokenStorage } from "../../../core/http-axios-react/http/Token";
import { ENV_LISTS } from "../../../core/EnvTranspiler";

export const useGetUsers = ({ keyword, page, perPage }: HttpBaseReq) => {
  const { redirectOnTokenExpired } = useErrorHandler();

  return useQuery({
    queryKey: `users${page}${keyword || ""}${perPage}`,
    queryFn: () => getUsers({ keyword, page, perPage }),
    onError: (e: AxiosError) => redirectOnTokenExpired(e),
  });
};

export const useGetUserDetail = ({ user_id }: { user_id: any }) => {
  const { redirectOnTokenExpired } = useErrorHandler();

  return useQuery({
    enabled: user_id !== null,
    queryKey: `userDetail${user_id}`,
    queryFn: () => getUserDetail({ user_id }),
    onError: (e: AxiosError) => redirectOnTokenExpired(e),
  });
};

export const useGetRoles = () => {
  const { redirectOnTokenExpired } = useErrorHandler();
  const hasToken = TokenStorage.get(ENV_LISTS.KEY_TOKEN);

  return useQuery({
    enabled: hasToken !== null,
    queryKey: `userRole`,
    queryFn: () => getUserRoles(),
    onError: (e: AxiosError) => redirectOnTokenExpired(e),
  });
};

export const usePostAddUser = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: `addUser`,
    mutationFn: ({ name, email, role, password }: IHttpPostUser) =>
      postAddUser({ name, email, role, password }),
    onSuccess: (e) => {
      toast.show("Berhasil menambahkan user baru");
      queryClient.invalidateQueries();
      navigate("/user");
    },
    onError: (e: any) => {
      const msg =
        e.message || "Terjadi kesalahan mohon coba beberapa saat lagi";
      toast.show(msg, "error");
    },
  });
};

export const usePatchUser = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: `patchUser`,
    mutationFn: ({ name, email, role, password, user_id }: IHttpPatchUser) =>
      patchUser({ name, email, role, password, user_id }),
    onSuccess: (e) => {
      toast.show("Berhasil update user");
      queryClient.invalidateQueries();
      navigate("/user");
    },
    onError: (e: any) => {
      const msg =
        e.message || "Terjadi kesalahan mohon coba beberapa saat lagi";
      toast.show(msg, "error");
    },
  });
};

export const useDeleteUser = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: "deleteUser",
    mutationFn: ({ user_id }: { user_id: number }) => deleteUser({ user_id }),
    onSuccess: (e) => {
      if (e.data.statusCode === 200) {
        toast.show("Berhasil delete user");
        queryClient.invalidateQueries();
      } else if (e.data.statusCode === 400 || e.data.statusCode === 500) {
        const msg =
          e.data.error_message ||
          "Terjadi kesalahan mohon coba beberapa saat lagi";
        toast.show(msg);
      }
    },
  });
};
